import { useEffect } from 'react';

import type { EnvironmentSummary } from '@/api/configuration';
import { useEnvironments } from '@/hooks/use-configuration';
import { useEnvironmentStore } from '@/stores/environment-store';

interface EnvironmentSelectorProps {
  projectKey: string;
}

/**
 * Top-level environment selector that syncs with the global environment store.
 * Auto-selects the first environment if none is selected.
 */
export function EnvironmentSelector({ projectKey }: EnvironmentSelectorProps) {
  const { data: environments = [], isLoading } = useEnvironments(projectKey);
  const { selectedEnvironmentId, setSelectedEnvironmentId } = useEnvironmentStore();

  // Auto-select first environment when environments load and nothing is selected
  useEffect(() => {
    if (environments.length > 0 && !selectedEnvironmentId) {
      setSelectedEnvironmentId(environments[0]?.id ?? '');
    }
  }, [environments, selectedEnvironmentId, setSelectedEnvironmentId]);

  // If previously selected environment is no longer available, reset to first
  useEffect(() => {
    if (
      environments.length > 0 &&
      selectedEnvironmentId &&
      !environments.some((e) => e.id === selectedEnvironmentId)
    ) {
      setSelectedEnvironmentId(environments[0]?.id ?? '');
    }
  }, [environments, selectedEnvironmentId, setSelectedEnvironmentId]);

  if (isLoading) {
    return (
      <div className="animate-pulse h-9 w-48 rounded-md bg-gray-200 dark:bg-gray-700" />
    );
  }

  if (environments.length === 0) {
    return (
      <p className="text-sm text-content-tertiary italic">
        No environments configured. Add environments in Settings.
      </p>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor="env-selector"
        className="text-sm font-medium text-content-secondary"
      >
        Environment
      </label>
      <select
        id="env-selector"
        value={selectedEnvironmentId}
        onChange={(e) => setSelectedEnvironmentId(e.target.value)}
        className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-content shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        {environments.map((env: EnvironmentSummary) => (
          <option key={env.id} value={env.id}>
            {env.name}
          </option>
        ))}
      </select>
    </div>
  );
}
